import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Stack,
  Typography,
} from "@mui/material";
import CustomDialog from "global/components/CustomDialog/CustomDialog";
import { useEffect, useState } from "react";
import { handleErrorMessage, openSuccessNotification } from "helpers/methods";
import { duplicateCampaign } from "../CampaignService";
import CustomLoader from "global/components/CustomLoader/CustomLoader";

interface CustomProps {
  isOpen: boolean;
  setOpen: Function;
  campaignId: number;
  refresh: Function;
}

const DuplicateModal = (props: CustomProps) => {
  const [loading, setLoading] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState<
    Record<string, boolean>
  >({
    all: false,
    fromId: false,
    subject: false,
    design: false,
    attachment: false,
    segment: false,
  });

  useEffect(() => {
    const { all: _all, ...rest } = selectedOptions;
    const allChecked = Object.values(rest).every((val) => val === true);

    setSelectedOptions((prev) => ({
      ...prev,
      all: allChecked,
    }));
  }, [
    selectedOptions.fromId,
    selectedOptions.subject,
    selectedOptions.design,
    selectedOptions.attachment,
    selectedOptions.segment,
  ]);

  const options = [
    {
      name: "fromId",
      label: "Sender",
      description: "Keep the original sender details",
    },
    {
      name: "segment",
      label: "Recipients",
      description: "Use the same recipient list as the original",
    },
    {
      name: "subject",
      label: "Subject",
      description: "Copy the subject line",
    },
    {
      name: "design",
      label: "Design",
      description: "Maintain the current design",
    },
    {
      name: "attachment",
      label: "Attachments",
      description: "Include the original attachments",
    },
  ];

  const handleClose = () => {
    props.setOpen(false);
    setSelectedOptions({});
  };

  const handleSaveDuplicateCampaign = async () => {
    try {
      setLoading(true);
      const res = await duplicateCampaign(props.campaignId, selectedOptions);
      openSuccessNotification(res.message);
      props.setOpen(false);
      props.refresh();
    } catch (error: any) {
      handleErrorMessage(error);
      props.setOpen(false);
    } finally {
      setLoading(false);
      props.setOpen(false);
    }
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;

    if (name === "all") {
      setSelectedOptions((prev) => {
        const newState = {
          ...prev,
          all: checked,
          fromId: checked,
          subject: checked,
          design: checked,
          attachment: checked,
          segment: checked,
        };
        return newState;
      });
    } else {
      setSelectedOptions((prev) => {
        const newState = {
          ...prev,
          [name]: checked,
        };
        const { all: _all, ...rest } = newState;
        const allChecked = Object.values(rest).every((val) => val === true);
        newState.all = allChecked;

        return newState;
      });
    }
  };

  const getBody = () => {
    return (
      <Stack spacing={1}>
        <Typography variant="body1">
          Select what you want to keep in the copied campaign.
        </Typography>
        <Stack spacing={1} sx={{ mt: 2 }}>
          {options.map((option) => (
            <Stack key={option.name}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedOptions[option.name]}
                    onChange={handleCheckboxChange}
                    name={option.name}
                    size="large"
                    sx={{ transform: "scale(1.3)" }}
                  />
                }
                label={
                  <Typography variant="body1" sx={{ fontSize: "1rem" }}>
                    {option.label}
                  </Typography>
                }
              />
              <Typography color="text.secondary" variant="body2">
                {option.description}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Stack>
    );
  };

  const getFooter = () => {
    return (
      <Stack direction="row" spacing={2}>
        <Button variant="outlined" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={Object.values(selectedOptions).filter(Boolean).length === 0}
          onClick={handleSaveDuplicateCampaign}
        >
          Duplicate Campaign
        </Button>
      </Stack>
    );
  };

  return (
    <>
      <CustomDialog
        handleDialogClose={handleClose}
        isDialogOpen={props.isOpen}
        closable
        closeButtonVisibility
        body={getBody()}
        title={
          <Typography variant="h4" color="#7551D0">
            Duplicate Campaign?
          </Typography>
        }
        footer={getFooter()}
      />
      <CustomLoader isLoading={loading} />
    </>
  );
};

export default DuplicateModal;
